import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import api, { MEDIA } from "../lib/api";

export default function Blog() {
    const [posts, setPosts] = React.useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeCat, setActiveCat] = React.useState(searchParams.get("cat") || null);
    React.useEffect(() => { api.get("/blog").then(r => setPosts(r.data || [])); }, []);

    const categories = [...new Set(posts.map(p => p.category).filter(Boolean))];
    const filtered = activeCat ? posts.filter(p => p.category === activeCat) : posts;
    const featured = filtered[0];
    const rest = filtered.slice(1);

    const fmtDate = (d) => new Date(d).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

    return (
        <div data-testid="blog-page">
            {/* Hero */}
            <section className="bg-foreground text-background rounded-b-[2rem] overflow-hidden">
                <div className="container-x py-14 md:py-20">
                    <div className="overline text-primary mb-3">Resurse & sfaturi</div>
                    <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight max-w-2xl">
                        Blog & ghiduri
                    </h1>
                    <p className="text-background/70 mt-4 max-w-xl">
                        Articole despre securitate, ghiduri de instalare și sfaturi pentru alegerea echipamentelor potrivite.
                    </p>
                </div>
            </section>

            <div className="container-x py-12 sm:py-16">
                {/* Category filter */}
                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-10">
                        <button
                            onClick={() => { setActiveCat(null); setSearchParams({}); }}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${!activeCat ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"}`}
                        >
                            Toate
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => { const nc = activeCat === cat ? null : cat; setActiveCat(nc); nc ? setSearchParams({ cat: nc }) : setSearchParams({}); }}
                                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeCat === cat ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* Featured post */}
                {featured && (
                    <div className="mb-10">
                        <Link to={`/blog/${featured.slug}`}
                            className="group grid grid-cols-1 lg:grid-cols-2 bg-card rounded-3xl overflow-hidden border border-border/60 hover:border-primary/30 hover:shadow-xl transition-all duration-300 animate-fade-up"
                            data-testid={`blog-featured-${featured.slug}`}>
                            <div className="aspect-[16/9] lg:aspect-auto bg-muted overflow-hidden">
                                {featured.image
                                    ? <img src={MEDIA(featured.image)} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />}
                            </div>
                            <div className="p-8 sm:p-10 flex flex-col justify-center">
                                <div className="flex items-center gap-2 text-muted-foreground text-xs mb-4">
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span>{fmtDate(featured.created_at)}</span>
                                    {featured.author && <><span>·</span><span>{featured.author}</span></>}
                                    {featured.category && <><span>·</span><span className="text-primary font-medium">{featured.category}</span></>}
                                </div>
                                <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-3 leading-tight">{featured.title}</h2>
                                <p className="text-muted-foreground line-clamp-3 mb-6">{featured.excerpt}</p>
                                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                                    Citește articolul <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </Link>
                    </div>
                )}

                {/* Rest of posts */}
                {rest.length > 0 && (
                    <>
                        {featured && <div className="overline mb-6">Toate articolele</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {(featured ? rest : filtered).map((p, i) => (
                                <Link key={p.post_id} to={`/blog/${p.slug}`}
                                    className="group bg-card rounded-2xl overflow-hidden border border-border/60 hover:border-primary/30 hover:shadow-lg transition-all duration-300 animate-fade-up flex flex-col"
                                    style={{ animationDelay: `${i * 0.06}s` }}
                                    data-testid={`blog-${p.slug}`}>
                                    <div className="aspect-[16/9] bg-muted overflow-hidden">
                                        {p.image
                                            ? <img src={MEDIA(p.image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            : <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />}
                                    </div>
                                    <div className="p-5 flex flex-col flex-1">
                                        <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-2">
                                            <Calendar className="h-3 w-3" />
                                            <span>{fmtDate(p.created_at)}</span>
                                            {p.category && <><span>·</span><span className="text-primary font-medium">{p.category}</span></>}
                                        </div>
                                        <h3 className="font-heading text-lg font-semibold leading-snug mb-2 line-clamp-2 flex-1">{p.title}</h3>
                                        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{p.excerpt}</p>
                                        <div className="flex items-center gap-1.5 text-primary text-xs font-semibold mt-auto">
                                            Citește <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {filtered.length === 0 && (
                    <div className="py-20 text-center text-muted-foreground">
                        <div className="text-4xl mb-4">✍️</div>
                        <p>{activeCat ? `Niciun articol în categoria "${activeCat}".` : "Nu există articole momentan. Revin curând!"}</p>
                    </div>
                )}
            </div>
        </div>
    );
}
